/**
 * Cut app actions — S2 cut-line → durable item pool.
 *
 * No new schema. The WHOLE ordered list is saved as the pool, in order, and the
 * pool's cut line sits where the user drew it — so the Item Pool Editor shows
 * the kept items above the line and the cut items below it. Typed candidates
 * become workspace items on both sides of the line. Then createItemPool +
 * updateRanking + updateCutLine.
 */

import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { generateUUID, getCurrentWorkspaceId } from '@/lib/supabaseSync';

export interface CutCandidate {
  /** Workspace item id when sourced from Items; empty for typed-only until create. */
  id?: string;
  title: string;
}

export interface SaveCutAsPoolInput {
  poolName: string;
  /** Ordered candidates — index 0 is top of the list. */
  ordered: CutCandidate[];
  /** Cut line = keep items with index < cutLine (same as ItemPool.cutLinePosition). */
  cutLine: number;
  ownerId: string;
  description?: string;
}

export async function saveCutAsItemPool(
  input: SaveCutAsPoolInput,
): Promise<{ success: boolean; poolId?: string; keptCount?: number; error?: string }> {
  const { poolName, ordered, cutLine, ownerId, description } = input;
  if (!poolName.trim()) {
    return { success: false, error: 'Name the pool before saving.' };
  }
  if (ordered.length < 2) {
    return { success: false, error: 'Add at least two candidates before cutting.' };
  }
  if (cutLine < 1 || cutLine > ordered.length) {
    return { success: false, error: 'Draw the cut so at least one item stays above the line.' };
  }

  const itemsStore = useItemsStore.getState();
  const poolsStore = useItemPoolsStore.getState();
  const workspaceId = getCurrentWorkspaceId() ?? undefined;

  // Every candidate goes into the pool, in order; keptCount is how many of
  // them sit above the line (ItemPool.cutLinePosition counts those).
  const orderedIds: string[] = [];
  let keptCount = 0;
  for (let index = 0; index < ordered.length; index++) {
    const candidate = ordered[index];
    let id = candidate.id;
    if (!id) {
      const title = candidate.title.trim();
      if (!title) continue;
      id = generateUUID();
      const created = await itemsStore.createItem({
        id,
        title,
        description: '',
        createdBy: ownerId,
        submittedBy: ownerId,
        tags: [],
        ...(workspaceId ? { workspaceId } : {}),
      });
      if (!created) {
        return { success: false, error: `Could not create item “${title}”.` };
      }
    }
    orderedIds.push(id);
    if (index < cutLine) keptCount += 1;
  }

  if (keptCount === 0) {
    return { success: false, error: 'Nothing stayed above the cut.' };
  }

  try {
    const poolId = await poolsStore.createItemPool({
      name: poolName.trim(),
      description: description?.trim() || `Cut kept ${keptCount} of ${orderedIds.length} candidates.`,
      ownerId,
      comparisonSetId: '',
      criteriaIds: [],
      cutLinePosition: 0,
      status: 'draft',
      tags: ['cut-app'],
      ...(workspaceId ? { workspaceId } : {}),
    } as Parameters<typeof poolsStore.createItemPool>[0]);

    poolsStore.updateRanking(poolId, orderedIds);
    // The cut line is governed (set_item_pool_cut_line, mig 760) and checked
    // against the pool's stored size, so the ranking must land first.
    const ranked = poolsStore.getItemPoolById(poolId);
    if (ranked) {
      if (ranked.priorityIds.length !== orderedIds.length) {
        return { success: false, error: 'The pool was created, but its items could not be added.' };
      }
      const synced = await poolsStore.syncToSupabase(ranked);
      if (!synced.success) {
        return { success: false, error: synced.error ?? 'Could not save the item pool order.' };
      }
    }
    const cut = await poolsStore.updateCutLine(poolId, keptCount);
    if (!cut.success) {
      return { success: false, error: cut.error ?? 'The pool was saved, but its cut line was not.' };
    }

    return { success: true, poolId, keptCount };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Could not save the item pool.',
    };
  }
}
